"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  label: string;
  value: number;
  prefix: string;
  suffix: string;
  display: string;
};
function CountUp({
  target,
  start,
  duration = 1100,
  delay = 0
}: {
  target: number;
  start: boolean;
  duration?: number;
  delay?: number;
}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!start) return;
    const timeoutId = window.setTimeout(() => {
      const startTime = performance.now();
      const easeOutCubic = (t: number) => {
        const c1 = 0.2;
        const c2 = 0.6;
        const c3 = 0.2;
        const c4 = 1;
        const u = 1 - t;
        return 3 * u * u * t * c1 + 3 * u * t * t * c3 + t * t * t * c4 + 0 * c2;
      };
      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(progress);
        setValue(Math.round(eased * target));
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setValue(target);
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    }, delay);
    return () => {
      window.clearTimeout(timeoutId);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [start, target, duration, delay]);
  return <>{value.toLocaleString("en-US")}</>;
}
export default function () {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const metrics: Metric[] = [{
    label: "STORIES AMPLIFIED",
    value: 450,
    prefix: "",
    suffix: "+",
    display: "count"
  }, {
    label: "COMMUNITY MEMBERS",
    value: 12,
    prefix: "",
    suffix: "K+",
    display: "thousands"
  }, {
    label: "UNBIASED REPORTS",
    value: 85,
    prefix: "",
    suffix: "%",
    display: "percentage"
  }, {
    label: "GLOBAL PERSPECTIVES",
    value: 60,
    prefix: "",
    suffix: "+",
    display: "count"
  }];
  return <section data-section-id="15334" id="community-impact-metrics" ref={ref} className="bg-background text-foreground py-20 md:py-24">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        {}
        <div className="relative mb-16 md:mb-20">
          <div className="absolute -top-6 left-0 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground" style={{
          lineHeight: 1
        }}>
            OUR COLLECTIVE REACH
          </div>
          <div className="h-px w-full origin-left bg-border transition-transform ease-out" style={{
          transitionDuration: "640ms",
          transform: isVisible ? "scaleX(1)" : "scaleX(0)"
        }} aria-hidden="true" />
        </div>

        {}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 md:gap-y-0 relative">
          {metrics.map((metric, i) => <div data-index={i} key={metric.label} className="relative px-0 md:px-8 flex flex-col">
              {}
              {i > 0 && <div className="hidden md:block absolute left-0 top-6 bottom-6 w-px origin-top bg-border transition-transform ease-out" style={{
            transitionDuration: "480ms",
            transitionDelay: `${i * 80}ms`,
            transform: isVisible ? "scaleY(1)" : "scaleY(0)"
          }} aria-hidden="true" />}

              {}
              {i >= 2 && <div className="md:hidden absolute -top-6 left-0 right-0 h-px bg-border" aria-hidden="true" />}

              {}
              <div className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground transition-all ease-out" style={{
            lineHeight: 1,
            transitionDuration: "520ms",
            transitionDelay: `${300 + i * 120}ms`,
            transitionProperty: "opacity, transform",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(24px)"
          }}>
                {metric.label}
              </div>

              {}
              <div className="mt-6 font-default font-bold text-foreground flex items-baseline transition-all ease-out" style={{
            fontSize: "clamp(3.5rem, 6vw, 6rem)",
            letterSpacing: "-0.03em",
            lineHeight: 0.92,
            transitionDuration: "520ms",
            transitionDelay: `${380 + i * 120}ms`,
            transitionProperty: "opacity, transform",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(24px)"
          }}>
                <span className="tabular-nums">
                  {metric.prefix}
                  <CountUp target={metric.value} start={isVisible} duration={1100} delay={380 + i * 120} />
                </span>
                {metric.suffix && <span style={{
              color: "var(--primary)"
            }}>
                    {metric.suffix}
                  </span>}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}